import { Button } from "react-bootstrap";
import {
  DragItem,
  ItemTypes,
  SortableProductRowProps,
} from "../../../../models/state/IStoreProduct";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { observer } from "mobx-react";
import { FormattedMessage } from "react-intl";
import { formatNumberWithCommas } from "../../../../constants/constants";

// Sortable product row component
export const SortableProductRow: React.FC<SortableProductRowProps> = ({
  id,
  product,
  index,
  categoryId,
  moveItem,
  onDetailClick,
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TREATMENT,
    item: (): DragItem => {
      return {
        type: ItemTypes.TREATMENT,
        id,
        index,
        parentId: categoryId,
        productId: product.Id,
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  interface DropResult {
    handlerId: string | symbol | null;
  }

  const [{ handlerId, isOver }, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null; isOver: boolean }>({
      accept: ItemTypes.TREATMENT,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
          isOver: monitor.isOver(),
        };
      },
      // Use canDrop to show visual feedback
      canDrop(item: DragItem, monitor) {
        const sourceCategoryId = item.parentId as string;
        const targetCategoryId = categoryId;
        // Only allow dropping within the same category
        return sourceCategoryId === targetCategoryId;
      },
      // Only execute on drop, not on hover
      drop(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      const sourceCategoryId = item.parentId as string;
      const targetCategoryId = categoryId;
      const hoverProductId = Number(product.Id);
      const dragProductId = Number(item.productId);

      // Don't replace items with themselves
      if (dragIndex === hoverIndex && sourceCategoryId === targetCategoryId) {
        return;
      }

      // Reject drop if trying to move between categories
      if (sourceCategoryId !== targetCategoryId) {
        return;
      }

      // Time to actually perform the action
      moveItem(
        dragIndex,
        hoverIndex,
        sourceCategoryId,
        targetCategoryId,
        dragProductId,
        hoverProductId
      );
    },
  });

  drag(drop(ref));

  const rowClassName = `border-top ${isDragging ? "dragging" : ""}`;

  return (
    <tr ref={ref} className={rowClassName} data-handler-id={handlerId}>
      <td>
        <i className="fas fa-grip-vertical drag-handle">
          <svg
            id="fi_8379631"
            height="12"
            viewBox="0 0 24 24"
            width="12"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              id="drag-vertical"
              d="m10 19a2 2 0 1 1 -2-2 2 2 0 0 1 2 2zm-2-9a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm8 14a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2z"
              fill="rgb(0,0,0)"
            ></path>
          </svg>
        </i>
      </td>
      <td>{index + 1}</td>
      <td>{product.ProductName}</td>
      <td><i className="bi bi-currency-yen me-1"></i> {formatNumberWithCommas(product.PriceIncludingTax)}</td>
      <td>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => onDetailClick(product)}
        >
        <FormattedMessage id="details" />
        </Button>
      </td>
    </tr>
  );
};

export default observer(SortableProductRow);
